import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Form, Input, Select, Switch, Button, Empty, Space, Tooltip, message, FormInstance, TimePicker } from "antd";
import { SaveOutlined, LinkOutlined, CopyOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import ImageWithSpinner from "../content/imagewithspinner";
import FormButton from "../common/formbutton";
import DrawerMenuOptions from "./drawermenuoptions";
import DrawerAddOns from "./draweraddons";
import DrawerPageTrait from "./drawerpagetrait";
import DrawerHighlights from "./drawerhighlights";
import { useAuth } from "../../auth/authcontext";
import SignatureSwitch from "../menuitems/signatureswitch";
import { lookupMenuItems } from "../../services/menuservice";
import { MenuItemLookup } from "../../types/menutypes";
import debounce from "lodash/debounce";

const { Option } = Select;
const { Item } = Form;

interface MenuItemImage {
    guid: string;
    name: string;
    upc: string;
    imageUrl: string;
    signatureItem: boolean;
    itemType?: number;
}

interface PageDetailsProps {
    form: FormInstance;
    menuItemImages: MenuItemImage[];
    handleDropdownSelection: (guid: string, option: any) => void;
    isMenuItem: boolean;
    handleMenuItemChange: (checked: boolean) => void;
    imageUrl: string | null;
    isOverrideImageUrl: boolean;
    handleImageUrlChange: (checked: boolean) => void;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleOk: () => void;
    isEditable: boolean;
    onUnsavedChanges?: (hasChanges: boolean) => void;
    loading: boolean;
    pageGuid: string;
    pageversionGuid: string;
}

const PageDetails: React.FC<PageDetailsProps> = ({
    form,
    menuItemImages,
    handleDropdownSelection,
    isMenuItem,
    handleMenuItemChange,
    imageUrl,
    isOverrideImageUrl,
    handleImageUrlChange,
    handleInputChange,
    handleOk,
    isEditable,
    onUnsavedChanges,
    loading: parentLoading,
    pageGuid,
    pageversionGuid
}) => {
    const [menuItemGuid, setMenuItemGuid] = useState<string | null>(null);
    const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
    const [addOnsDrawerVisible, setAddOnsDrawerVisible] = useState<boolean>(false);
    const [pageTraitDrawerVisible, setPageTraitDrawerVisible] = useState(false);
    const [isSignatureItem, setIsSignatureItem] = useState<boolean>(false);

    const [source, setSource] = useState<"menu" | "product" | null>(null);
    const [items, setItems] = useState<MenuItemLookup[]>([]);
    const [loadingItems, setLoadingItems] = useState(false);

    const { userId, isReadOnly } = useAuth();
    const userGuid = userId || null;
    const isDisabled = !isEditable || isReadOnly;
    const [initialValues, setInitialValues] = useState<Record<string, any> | undefined>(undefined);
    const itemMarginBottom = "10px";
    const [highlightsDrawerVisible, setHighlightsDrawerVisible] = useState(false);

    const showHighlightsDrawer = () => setHighlightsDrawerVisible(true);
    const closeHighlightsDrawer = () => setHighlightsDrawerVisible(false);

    const isHero = Form.useWatch("isHeroItem", form);
    const bannerImageUrl = Form.useWatch("bannerImage", form);

    const fetchLookupItems = async (query = "", selectedSource = source) => {
        if (!selectedSource) return;
        setLoadingItems(true);
        try {
            const res = await lookupMenuItems(selectedSource, query, 20);
            setItems(res.items);
        } catch (error) {
            console.error("Failed to load lookup items", error);
        } finally {
            setLoadingItems(false);
        }
    };

    const debouncedSearch = useMemo(
        () => debounce((value: string) => fetchLookupItems(value), 300),
        [source]
    );

    const handleSourceChange = (val: "menu" | "product") => {
        setSource(val);
        setItems([]);
        form.setFieldsValue({
            menuItem: null,
            selectedImageUrl: null,
            signatureItem: false,
        });
        form.setFieldValue("source", val);
        fetchLookupItems("", val);
    };

    const handleSelectItem = (menuGuid: string, option: any) => {
        handleDropdownSelection(menuGuid, option);
    };

    useEffect(() => {
        if (!parentLoading && menuItemImages.length > 0) {
            const currentMenuItemGuid = form.getFieldValue("menuItem");
            if (currentMenuItemGuid && currentMenuItemGuid !== "(SELECT)") {
                const matchedItem = menuItemImages.find(o => o.guid === currentMenuItemGuid);
                if (matchedItem) {
                    const inferredSource = matchedItem.itemType === 0 ? "product" : "menu";
                    setSource(inferredSource);
                    form.setFieldsValue({
                        source: inferredSource,
                        menuItem: currentMenuItemGuid
                    });
                    
                    setItems([{
                        id: inferredSource === "product" ? matchedItem.upc : matchedItem.guid,
                        menuGuid: matchedItem.guid,
                        name: matchedItem.name,
                        imageUrl: matchedItem.imageUrl,
                        signatureItem: matchedItem.signatureItem
                    }]);
                }
            }
        }
    }, [parentLoading, menuItemImages, form]);

    useEffect(() => {
        if (!parentLoading && !initialValues) {
            const initial = {
                name: form.getFieldValue("name") || "",
                isMenuItem: form.getFieldValue("isMenuItem") || false,
                source: form.getFieldValue("source") || null,
                menuItem: form.getFieldValue("menuItem") || null,
                signatureItem: form.getFieldValue("signatureItem") || false,
                addImage: form.getFieldValue("addImage") || false,
                selectedImageUrl: form.getFieldValue("selectedImageUrl") || null,
                isHeroItem: form.getFieldValue("isHeroItem") ?? false,
                bannerImage: form.getFieldValue("bannerImage") ?? null,
                heroBadgeText: form.getFieldValue("heroBadgeText") ?? 'FEATURED',
                heroBadgeStyle: form.getFieldValue("heroBadgeStyle") ?? 'orange',
                startTime: form.getFieldValue("startTime") || null,
                endTime: form.getFieldValue("endTime") || null,
            };
            setInitialValues(initial);
            form.setFieldsValue(initial);
            onUnsavedChanges?.(false);
        }
    }, [form, parentLoading, onUnsavedChanges, initialValues]);

    const compareValues = useCallback((currentValues: Record<string, any>): boolean => {
        if (!initialValues) return false;

        // List all fields you want to track
        const keys = [
            "name",
            "isMenuItem",
            "source",
            "menuItem",
            "signatureItem",
            "addImage",
            "selectedImageUrl",
            "isHeroItem",
            "bannerImage",
            "heroBadgeText",
            "heroBadgeStyle",
            "startTime",
            "endTime",
        ];

        const normalize = (v: any) =>
            typeof v === "string" ? v.trim() : v;

        // Loop and compare
        return keys.some((k) => {
            const initialVal = initialValues[k];
            const currentVal = currentValues[k];

            if (dayjs.isDayjs(initialVal) || dayjs.isDayjs(currentVal)) {
                const initTime = initialVal ? dayjs(initialVal).format("HH:mm:ss") : null;
                const currTime = currentVal ? dayjs(currentVal).format("HH:mm:ss") : null;
                return initTime !== currTime;
            }

            return normalize(initialVal) !== normalize(currentVal);
        });
    }, [initialValues]);

    const handleSaveAndReset = () => {
        handleOk();
        setInitialValues(form.getFieldsValue());
        onUnsavedChanges?.(false);
    };

    const onFormValuesChange = useCallback(
        (_changedValues: any, allValues: Record<string, any>) => {
            const hasChanged = compareValues(allValues);
            onUnsavedChanges?.(hasChanged);
        },
        [compareValues, onUnsavedChanges]
    );

    const handleUserImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleInputChange(e);
    };

    const showDrawer = () => {
        setDrawerVisible(true);
        setMenuItemGuid(form.getFieldValue("menuItem"));
        setIsSignatureItem(form.getFieldValue("signatureItem"));
    };

    const showAddOnsDrawer = () => {
        setAddOnsDrawerVisible(true);
        setMenuItemGuid(form.getFieldValue("menuItem"));
    };

    const closeDrawer = () => {
        setMenuItemGuid(null);
        setDrawerVisible(false);
    };

    const closeAddOnsDrawer = () => {
        setMenuItemGuid(null);
        setAddOnsDrawerVisible(false);
    };

    const showPageTraitDrawer = () => setPageTraitDrawerVisible(true);
    const closePageTraitDrawer = () => setPageTraitDrawerVisible(false);

    return (
        <>
            <Form
                labelCol={{ flex: "220px" }}
                labelAlign="right"
                labelWrap
                wrapperCol={{ flex: 1 }}
                colon
                style={{ maxWidth: 950 }}
                form={form}
                onValuesChange={onFormValuesChange}
                initialValues={initialValues}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px' }}>

                    <Item
                        name="image"
                        style={{ marginBottom: itemMarginBottom, flex: '0 0 150px' }}
                        wrapperCol={{ offset: 0, span: 24 }}
                    >
                        <div style={{ width: '180px', height: '180px' }}>
                            {imageUrl ? (
                                <ImageWithSpinner imageUrl={imageUrl} />
                            ) : (
                                <Empty description="Image Not Added" />
                            )}
                        </div>
                    </Item>

                    <div style={{ flex: '1 1 60%', maxWidth: 'calc(100% - 170px)' }}>
                        <Item
                            name="name"
                            label="Name"
                            rules={[{ required: true, message: 'Please enter the name.' }]}
                            style={{ marginBottom: itemMarginBottom }}
                        >
                            <Input style={{ borderRadius: '8px' }} disabled={isDisabled} />
                        </Item>

                        <Item label="Visibility Hours" style={{ marginBottom: itemMarginBottom }}>
                            <Space size="middle" style={{ display: 'flex' }}>
                                <Item
                                    name="startTime"
                                    noStyle
                                >
                                    <TimePicker
                                        placeholder="Start Time"
                                        format="HH:mm"
                                        disabled={isDisabled}
                                        style={{ borderRadius: '8px', width: '150px' }}
                                    />
                                </Item>
                                <span>to</span>
                                <Item
                                    name="endTime"
                                    noStyle
                                >
                                    <TimePicker
                                        placeholder="End Time"
                                        format="HH:mm"
                                        disabled={isDisabled}
                                        style={{ borderRadius: '8px', width: '150px' }}
                                    />
                                </Item>
                            </Space>
                        </Item>

                        <Item
                            name="isMenuItem"
                            label="Is Page a Menu Item?"
                            valuePropName="checked"
                            style={{ marginBottom: itemMarginBottom }}
                        >
                            <Switch
                                onChange={handleMenuItemChange}
                                checkedChildren="Yes"
                                unCheckedChildren="No"
                                disabled={isDisabled}
                            />
                        </Item>

                        {isMenuItem && (
                            <>
                                <Item
                                    name="source"
                                    label="Source"
                                    style={{ marginBottom: itemMarginBottom }}
                                    rules={[{ required: true, message: 'Please select a source.' }]}
                                >
                                    <Select
                                        placeholder="Select source"
                                        disabled={isDisabled}
                                        onChange={handleSourceChange}
                                    >
                                        <Option value="menu">Menu</Option>
                                        <Option value="product">Product</Option>
                                    </Select>
                                </Item>

                                <Item
                                    name="menuItem"
                                    label="Menu Item"
                                    style={{ marginBottom: itemMarginBottom }}
                                    rules={[{ required: true, message: 'Please select an item.' }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Search by name or UPC"
                                        disabled={isDisabled || !source}
                                        loading={loadingItems}
                                        filterOption={false}
                                        onSearch={debouncedSearch}
                                        onDropdownVisibleChange={(open) => {
                                            if (open && items.length === 0 && source) {
                                                fetchLookupItems("");
                                            }
                                        }}
                                        onChange={handleSelectItem}
                                    >
                                        {items.map((option) => (
                                            <Option
                                                key={option.menuGuid}
                                                value={option.menuGuid}
                                                dataImageUrl={option.imageUrl}
                                                dataSignatureItem={option.signatureItem}
                                                dataUpc={option.posCode}
                                            >
                                                {option.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Item>
                                <Tooltip title="Updating Is Signature Item? would also change this value at Menu Item level.">
                                    <Item
                                        name="signatureItem"
                                        label="Is Signature Item?"
                                        valuePropName="checked"
                                        style={{ marginBottom: itemMarginBottom }}
                                    >
                                        <SignatureSwitch
                                            form={form}
                                            menuItemGuid={form.getFieldValue("menuItem")}
                                        />
                                    </Item>
                                </Tooltip>
                            </>
                        )}

                        {isMenuItem
                            && form.getFieldValue('menuItem')
                            && form.getFieldValue('menuItem') !== "(SELECT)"
                            && (
                                <Tooltip title="Updating Additional Options? would also change this value at Menu Item level.">
                                    <Item label={isMenuItem ? "Additional Options" : ""} style={{ marginBottom: itemMarginBottom }}>
                                        <Space direction='vertical'>
                                            <Space >
                                                <FormButton
                                                    title={"Menu Options"}
                                                    onClick={showDrawer}
                                                />
                                                <FormButton
                                                    title={"Add Ons"}
                                                    onClick={showAddOnsDrawer}
                                                />
                                            </Space>
                                        </Space>
                                    </Item>
                                </Tooltip>
                            )}

                        {!isMenuItem && (

                            <Item label="Page Options" style={{ marginBottom: itemMarginBottom }}>
                                <Space direction='vertical'>
                                    <Space >
                                        <FormButton
                                            title="Set Page Trait"
                                            onClick={showPageTraitDrawer}
                                        />
                                        <FormButton
                                            title="Set Page Highlights"
                                            onClick={showHighlightsDrawer}
                                        />
                                    </Space>
                                </Space>
                            </Item>
                        )}


                        <Item
                            name="addImage"
                            label="Want to add image of your choice?"
                            valuePropName="checked"
                            style={{ marginBottom: itemMarginBottom }}
                        >
                            <Switch
                                onChange={handleImageUrlChange}
                                checkedChildren="Yes"
                                unCheckedChildren="No"
                                disabled={isDisabled}
                            />
                        </Item>

                        {isOverrideImageUrl && (
                            <Item name="selectedImageUrl" label="Image Url" style={{ marginBottom: itemMarginBottom }}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value?: string) {
                                            // Basic URL format check
                                            if (value && !/^https?:\/\/.+/i.test(value)) {
                                                return Promise.reject(new Error("Please enter a valid URL (http/https)."));
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Input
                                            style={{ flex: 1 }}
                                            prefix={<LinkOutlined />}
                                            placeholder="Enter image URL"
                                            onChange={handleUserImageUrlChange}
                                            value={form.getFieldValue('selectedImageUrl')}
                                            title={form.getFieldValue('selectedImageUrl') || ''}
                                        />
                                        <Button
                                            type="primary"
                                            shape="round"
                                            size="small"
                                            icon={<CopyOutlined />}
                                            style={{ padding: '0 10px', fontSize: '12px' }}
                                            onClick={() => {
                                                const url = form.getFieldValue('selectedImageUrl');
                                                if (url) {
                                                    navigator.clipboard.writeText(url);
                                                    message.success('URL copied to clipboard!');
                                                } else {
                                                    message.warning('No URL to copy');
                                                }
                                            }}
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                    {isMenuItem && (
                                        <span style={{ fontSize: '12px', color: '#888' }}>
                                            Updating Image URL will override the image for a menu item across all pages in the current page version.
                                        </span>
                                    )}
                                </div>
                            </Item>
                        )}

                        {/* Is Hero Item */}
                        <Item
                            name="isHeroItem"
                            label="Is Hero Item?"
                            valuePropName="checked"
                            style={{ marginBottom: itemMarginBottom }}
                        >
                            <Switch
                                checkedChildren="Yes"
                                unCheckedChildren="No"
                                disabled={isDisabled}
                                onChange={(checked) => {
                                    if (checked) {
                                        form.setFieldsValue({ bannerImage: undefined });
                                    }
                                }}
                            />
                        </Item>

                        {/* Hero Item Badge Customization */}
                        {isHero && (
                            <>
                                <Item
                                    name="heroBadgeText"
                                    label="Hero Badge Text"
                                    style={{ marginBottom: itemMarginBottom }}
                                    rules={[
                                        { required: true, message: 'Please enter hero badge text.' },
                                        { max: 20, message: 'Hero badge text cannot exceed 20 characters.' }
                                    ]}
                                >
                                    <Input 
                                        placeholder="E.g. NEW!, SALE!, MUST TRY!" 
                                        disabled={isDisabled}                                        
                                        maxLength={20}
                                        showCount
                                     />
                                </Item>
                                <Item
                                    name="heroBadgeStyle"
                                    label="Hero Badge Style"
                                    style={{ marginBottom: itemMarginBottom }}
                                    rules={[{ required: true, message: 'Please select hero badge style.' }]}
                                >
                                    <Select disabled={isDisabled}>
                                        {[
                                            { value: "orange", label: "Orange (Default)", color: "#ff6f00" },
                                            { value: "blue", label: "Blue", color: "#0052cc" },
                                            { value: "green", label: "Green", color: "#22c55e" },
                                            { value: "red", label: "Red", color: "#ef4444" },
                                            { value: "purple", label: "Purple", color: "#a855f7" }
                                        ].map(c => (
                                            <Option key={c.value} value={c.value}>
                                                <Space align="center">
                                                    <span 
                                                        style={{ 
                                                            display: 'inline-block', 
                                                            width: 12, 
                                                            height: 12, 
                                                            borderRadius: '50%', 
                                                            backgroundColor: c.color,
                                                            boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.15)'
                                                        }} 
                                                    />
                                                    <span>{c.label}</span>
                                                </Space>
                                            </Option>
                                        ))}
                                    </Select>
                                </Item>
                            </>
                        )}

                        {/* Banner Image (hidden when hero is ON) */}
                        {!isHero && (
                            <Item
                                name="bannerImage"
                                label="Banner Image URL"
                                style={{ marginBottom: itemMarginBottom }}
                                extra={
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        Recommended banner size: <strong>1920×640</strong> (aspect ratio <strong>3:1</strong>)
                                    </span>
                                }
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value?: string) {
                                            const hero = getFieldValue("isHeroItem");

                                            // Guard against programmatic conflicts
                                            if (hero && value) {
                                                return Promise.reject(
                                                    new Error("Cannot set a Banner Image when the page is a Hero Item.")
                                                );
                                            }

                                            // Basic URL format check
                                            if (value && !/^https?:\/\/.+/i.test(value)) {
                                                return Promise.reject(new Error("Please enter a valid URL (http/https)."));
                                            }

                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <Input prefix={<LinkOutlined />} placeholder="Enter banner image URL" title={bannerImageUrl || ''} />
                            </Item>
                        )}

                        {/* Only show when NOT hero and URL exists */}
                        {!isHero && bannerImageUrl && (
                            <Item label=" " colon={false} style={{ marginBottom: itemMarginBottom }} >

                                <div style={{ marginTop: -4 }}>
                                    <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                                        Banner Image Preview
                                    </div>
                                    <div
                                        style={{
                                            width: '100%',
                                            maxWidth: 420,
                                            height: 140,
                                            border: '1px dashed #eee',
                                        }}
                                    >
                                        <ImageWithSpinner imageUrl={bannerImageUrl} />
                                    </div>
                                </div>
                            </Item>
                        )}

                        <Item
                            wrapperCol={{ span: 24 }}
                            style={{ marginTop: '20px' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <Button
                                    color="green"
                                    type="primary"
                                    variant="solid"
                                    size="small"
                                    style={{
                                        fontSize: '12px',
                                        marginTop: '10px'
                                    }}
                                    icon={<SaveOutlined />}
                                    onClick={handleSaveAndReset}
                                    disabled={isDisabled}
                                >
                                    Save Changes
                                </Button>
                            </div>
                        </Item>
                    </div>
                </div>

            </Form>

            <DrawerMenuOptions
                drawerVisible={drawerVisible}
                closeDrawer={closeDrawer}
                menuItemGuid={menuItemGuid}
                isDisabled={isDisabled || form.getFieldValue("signatureItem") === true}
                isSignatureItem={isSignatureItem}
            />
            <DrawerAddOns
                drawerVisible={addOnsDrawerVisible}
                closeDrawer={closeAddOnsDrawer}
                menuItemGuid={menuItemGuid}
                isDisabled={isDisabled}
            />
            <DrawerPageTrait
                drawerVisible={pageTraitDrawerVisible}
                closeDrawer={closePageTraitDrawer}
                isDisabled={isDisabled}
                pageGuid={pageGuid}
                pageversionGuid={pageversionGuid}
            />
            <DrawerHighlights
                drawerVisible={highlightsDrawerVisible}
                closeDrawer={closeHighlightsDrawer}
                isDisabled={isDisabled}
                pageGuid={pageGuid}
                createdBy={userGuid}
            />
        </>
    );
};

export default PageDetails;